import { IgChip, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { ExperienceStatus, ScheduleDto } from "@intelligems/ig-types";
import { officialStatusName } from "@intelligems/ig-utils";
import { Skeleton, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetExperienceSchedules } from "@src/api/schedules/get-schedules-by-experience-id";
import { ExperienceDurationTooltip } from "@src/features/experiments/components/ExperienceDurationTooltip";
import { Experience } from "@src/store/slices/experiences/types";
import { RuntimeLabel } from "@src/domains/experienceTablePages/ExperienceModuleTableHelpers";
import { Dot } from "lucide-react";

interface StatusRendererProps {
  experience: Experience;
  variant?: "body2";
  // When the caller already has the schedules (e.g. the experiences list was
  // fetched with includeSchedules), pass them in to skip the per-row fetch.
  schedules?: ScheduleDto[];
  displayVariant: "chip" | "textAndDot" | "dot";
  showRunTime?: boolean;
}

export const StatusRenderer = ({
  experience,
  variant,
  schedules: providedSchedules,
  displayVariant,
  showRunTime,
}: StatusRendererProps) => {
  const scheduleQuery = useGetExperienceSchedules(experience.id, {
    enabled: providedSchedules === undefined,
  });
  const organization = useGetOrg();
  const theme = useTheme();

  const schedules = providedSchedules ?? scheduleQuery.data?.schedules ?? [];

  if (schedules.length) {
    if (displayVariant === "dot") {
      return (
        <StatusIcon
          status={experience.status}
          isArchived={Boolean(experience.archivedAtTs)}
        />
      );
    } else {
      return (
        <IgTooltip
          title={
            <ExperienceDurationTooltip
              experience={experience}
              organization={organization.data.org}
              schedules={schedules}
            />
          }
        >
          <IgChip
            size={"small"}
            sx={{
              backgroundColor: `${displayVariant === "chip" ? "white" : "transparent"} !important`,
            }}
            variant={
              "viewMode" in experience && experience.viewMode !== undefined
                ? "outlined"
                : undefined
            }
            label={
              <Grid
                container
                columnSpacing={1}
                alignItems={"center"}
                flexWrap={"nowrap"}
              >
                <Grid>
                  <StatusIcon
                    status={experience.status}
                    isArchived={Boolean(experience.archivedAtTs)}
                  />
                </Grid>
                <Grid>
                  <IgTypography
                    variant={variant}
                    color={
                      displayVariant === "textAndDot"
                        ? theme.palette.text.primary
                        : theme.palette.text.secondary
                    }
                  >
                    {experience.status === "pending"
                      ? "Scheduled"
                      : experience?.archivedAtTs
                        ? "Archived"
                        : `${officialStatusName(experience.status)} in Schedule`}
                  </IgTypography>
                  {showRunTime && Boolean(experience.startedAtTs) && (
                    <RuntimeLabel
                      experienceActions={experience.experienceActions}
                      hasExpEverStarted={Boolean(experience.startedAtTs)}
                    />
                  )}
                </Grid>
              </Grid>
            }
          />
        </IgTooltip>
      );
    }
  }

  if (displayVariant === "dot") {
    return (
      <StatusIcon
        status={experience.status}
        isArchived={Boolean(experience.archivedAtTs)}
      />
    );
  }
  return (
    <IgTooltip
      title={
        <ExperienceDurationTooltip
          experience={experience}
          organization={organization.data.org}
          schedules={schedules}
        />
      }
    >
      <IgChip
        size={"small"}
        sx={{
          backgroundColor: `${displayVariant === "chip" ? "white" : "transparent"} !important`,
          ...(displayVariant !== "chip" && {
            border: "none",
          }),
        }}
        variant={
          "viewMode" in experience && experience.viewMode !== undefined
            ? "outlined"
            : undefined
        }
        label={
          <Grid
            container
            columnSpacing={1}
            alignItems={"center"}
            flexWrap={"nowrap"}
          >
            <Grid>
              <StatusIcon
                status={experience.status}
                isArchived={Boolean(experience.archivedAtTs)}
              />
            </Grid>
            <Grid>
              <IgTypography
                variant={variant}
                color={
                  displayVariant === "textAndDot"
                    ? theme.palette.text.primary
                    : theme.palette.text.secondary
                }
              >
                {experience?.archivedAtTs
                  ? "Archived"
                  : officialStatusName(experience.status)}
              </IgTypography>
              {showRunTime && Boolean(experience.startedAtTs) && (
                <>
                  <Dot size={16} color={theme.palette.text.secondary} />

                  <RuntimeLabel
                    experienceActions={experience.experienceActions}
                    hasExpEverStarted={Boolean(experience.startedAtTs)}
                  />
                </>
              )}
            </Grid>
          </Grid>
        }
      />
    </IgTooltip>
  );
};

export const StatusIcon = ({
  status,
  isArchived,
}: {
  status: ExperienceStatus;
  isArchived: boolean;
}) => {
  const theme = useTheme();

  const SkeletonColors = {
    pending: theme.igBackgroundColors.interactive.dark.disabled,
    started: theme.igBackgroundColors.interactive.success.default,
    paused: theme.igBackgroundColors.interactive.warning.default,
    ended: theme.igBackgroundColors.interactive.error.default,
    archived: theme.igBackgroundColors.interactive.dark.default,
  };

  return (
    <Skeleton
      animation={false}
      variant={"circular"}
      sx={{
        width: "6px",
        height: "6px",
        backgroundColor: isArchived
          ? SkeletonColors.archived
          : SkeletonColors[status],
      }}
    />
  );
};
